import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAudioCommentsPagePath, trackIdFromAudioCommentsPath } from '../utils/appRoutes';
import { ArrowLeft } from 'lucide-react';

interface HeaderBackButtonProps {
  className?: string;
  toDashboard?: boolean;
  onClick?: () => void;
}

const HeaderBackButton: React.FC<HeaderBackButtonProps> = ({ 
  className, 
  toDashboard, 
  onClick 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  
  const isDashboard = location.pathname === '/dashboard';
  
  const handleClick = () => {
    if (isDashboard) return; // Don't do anything on dashboard
    
    if (onClick) {
      onClick();
    } else if (toDashboard) {
      navigate('/dashboard');
    } else {
      const pathname = location.pathname;
      
      if (isAudioCommentsPagePath(pathname)) {
        const trackId = trackIdFromAudioCommentsPath(pathname);
        if (trackId) {
          navigate(`/tracks/${trackId}`);
          return;
        }
      }
      
      // Collaborators page: /tracks/:id/collaborators
      // Always navigate explicitly to track detail (prevents tab closing in new tabs)
      const collaboratorsMatch = pathname.match(/^\/tracks\/([^/]+)\/collaborators$/);
      if (collaboratorsMatch) {
        navigate(`/tracks/${collaboratorsMatch[1]}`);
        return;
      }
      
      // Track detail page: /tracks/:id
      // Check if we can safely go back, otherwise go to dashboard
      const trackDetailMatch = pathname.match(/^\/tracks\/([^/]+)$/);
      if (trackDetailMatch) {
        // Check if there's valid history from within the app
        // If history.length is 1, we're in a new tab/window, so go to dashboard
        if (window.history.length > 1) {
          // Try browser history first (user came from dashboard or elsewhere)
          navigate(-1);
        } else {
          // No history (direct link or new tab), go to dashboard
          navigate('/dashboard');
        }
        return;
      }
      
      // For all other pages, use browser history (original behavior)
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        // Fallback to dashboard if no history
        navigate('/dashboard');
      }
    }
  };
  
  return (
    <button
      onClick={handleClick}
      disabled={isDashboard}
      className={`
        flex items-center justify-center
        w-10 h-10
        rounded-lg
        transition-colors duration-200
        ${isDashboard 
          ? 'invisible cursor-default' 
          : 'hover:bg-[var(--color-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--btn-primary-ring)] text-[var(--color-text)] hover:text-[var(--brand-from)]'
        }
        ${className || ''}
      `}
      aria-label={isDashboard ? undefined : t('buttons.back')}
      data-testid="header-back-button"
    >
      <ArrowLeft className="h-5 w-5" />
    </button>
  );
};

export default HeaderBackButton;
